import { createFileRoute, useRouter } from '@tanstack/react-router'
import { assetPath } from '@/utils/assetPath'
import { useEffect, useState } from 'react'
import { useCurrentUser } from '@/hooks/useCurrentUser'

type ExtensionAuthCallbackSearchParams = {
    state?: string
    source?: string
    token?: string
    refreshToken?: string
    error?: string
}

export const Route = createFileRoute(
    '/(onboarding)/(auth)/(extension)/extension-auth-callback'
)({
    validateSearch: (
        search: Record<string, unknown>
    ): ExtensionAuthCallbackSearchParams => {
        return {
            state: typeof search.state === 'string' ? search.state : undefined,
            source:
                typeof search.source === 'string' ? search.source : undefined,
            token: typeof search.token === 'string' ? search.token : undefined,
            refreshToken:
                typeof search.refreshToken === 'string'
                    ? search.refreshToken
                    : undefined,
            error: typeof search.error === 'string' ? search.error : undefined,
        }
    },
    component: ExtensionAuthCallbackRoute,
})

function ExtensionAuthCallbackRoute() {
    const router = useRouter()
    const {
        state,
        source,
        token: urlToken,
        refreshToken: urlRefreshToken,
        error: urlError,
    } = Route.useSearch()
    const { user } = useCurrentUser()
    const [status, setStatus] = useState<'processing' | 'success' | 'error'>(
        'processing'
    )
    const [message, setMessage] = useState('Processing authentication...')

    useEffect(() => {
        handleExtensionCallback()
    }, [user, state, source, urlToken, urlRefreshToken, urlError])

    const handleExtensionCallback = async () => {
        try {
            // Validate that this is coming from the extension
            if (source !== 'extension') {
                throw new Error('Invalid source parameter')
            }

            // Check for error in URL
            if (urlError) {
                throw new Error(decodeURIComponent(urlError))
            }

            // If we have tokens in URL, use them directly (from login redirect)
            if (urlToken) {
                if (urlRefreshToken) {
                    // We have both tokens, use the new method
                    await redirectToExtensionWithTokens(
                        urlToken,
                        urlRefreshToken,
                        state
                    )
                } else {
                    // Only ID token, use legacy method
                    await redirectToExtension(urlToken, state)
                }
                return
            }

            // Otherwise, we should have a logged-in user
            if (!user) {
                // User needs to log in - redirect to login with proper return path
                const currentUrl = new URL(window.location.href)
                const loginUrl = `/login?returnToPath=${encodeURIComponent(currentUrl.pathname + currentUrl.search)}`
                router.navigate({ to: loginUrl })
                return
            }

            // Get the user's current authentication tokens
            const tokens = await getCurrentUserTokens()
            if (!tokens) {
                throw new Error('Failed to get authentication tokens')
            }

            await redirectToExtensionWithTokens(
                tokens.idToken,
                tokens.refreshToken,
                state
            )
        } catch (error) {
            console.error('Extension auth callback error:', error)
            setStatus('error')
            setMessage(
                error instanceof Error ? error.message : 'Authentication failed'
            )

            // Redirect to extension with error after a short delay
            setTimeout(() => {
                redirectToExtensionWithError(
                    error instanceof Error
                        ? error.message
                        : 'Authentication failed',
                    state
                )
            }, 3000)
        }
    }

    const getCurrentUserTokens = async (): Promise<{
        idToken: string
        refreshToken: string
    } | null> => {
        try {
            // Get both ID token and refresh token from Firebase
            const { getAuth } = await import('firebase/auth')
            const auth = getAuth()
            const currentUser = auth.currentUser

            if (!currentUser) {
                throw new Error('No authenticated user found')
            }

            const idToken = await currentUser.getIdToken()
            const refreshToken = currentUser.refreshToken

            if (!refreshToken) {
                throw new Error('No refresh token available')
            }

            return { idToken, refreshToken }
        } catch (error) {
            console.error('Failed to get user tokens:', error)
            return null
        }
    }

    const redirectToExtension = async (token: string, state?: string) => {
        try {
            // Construct the callback URL with the token and state
            const callbackUrl = new URL(window.location.href)
            callbackUrl.searchParams.set('token', token)
            if (state) {
                callbackUrl.searchParams.set('state', state)
            }

            // Update the current URL to include the token
            window.history.replaceState({}, '', callbackUrl.toString())

            setStatus('success')
            setMessage('Authentication successful! You can close this tab.')

            // The Chrome extension's background script will detect this URL change
            // and handle the token extraction
        } catch (error) {
            console.error('Failed to redirect to extension:', error)
            throw error
        }
    }

    const redirectToExtensionWithTokens = async (
        idToken: string,
        refreshToken: string,
        state?: string
    ) => {
        try {
            // Construct the callback URL with both tokens and state
            const callbackUrl = new URL(window.location.href)
            callbackUrl.searchParams.set('token', idToken)
            callbackUrl.searchParams.set('refreshToken', refreshToken)
            if (state) {
                callbackUrl.searchParams.set('state', state)
            }

            // Update the current URL to include the tokens
            window.history.replaceState({}, '', callbackUrl.toString())

            setStatus('success')
            setMessage('Authentication successful! You can close this tab.')

            // The Chrome extension's background script will detect this URL change
            // and handle the token extraction
        } catch (error) {
            console.error('Failed to redirect to extension:', error)
            throw error
        }
    }

    const redirectToExtensionWithError = (
        errorMessage: string,
        state?: string
    ) => {
        try {
            const callbackUrl = new URL(window.location.href)
            callbackUrl.searchParams.set(
                'error',
                encodeURIComponent(errorMessage)
            )
            if (state) {
                callbackUrl.searchParams.set('state', state)
            }

            window.history.replaceState({}, '', callbackUrl.toString())
        } catch (error) {
            console.error('Failed to redirect with error:', error)
        }
    }

    return (
        <div className="flex min-h-dvh items-center justify-center bg-linear-to-br from-purple-900 via-blue-900 to-indigo-900 p-4 pt-safe">
            <div className="w-full max-w-md rounded-xl border border-white/20 bg-white/10 p-8 text-center backdrop-blur-md">
                <div className="mb-6">
                    <img
                        src={assetPath('images/rectangle-logo.png')}
                        alt="Style Collection"
                        className="mx-auto mb-4 h-8"
                    />
                    <h1 className="mb-2 text-2xl font-semibold text-white">
                        Extension Authentication
                    </h1>
                </div>

                <div className="mb-6">
                    {status === 'processing' && (
                        <div className="mb-4 flex items-center justify-center">
                            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-white"></div>
                        </div>
                    )}

                    {status === 'success' && (
                        <div className="mb-4 flex items-center justify-center">
                            <div className="rounded-full bg-green-500 p-2">
                                <svg
                                    className="h-6 w-6 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M5 13l4 4L19 7"
                                    />
                                </svg>
                            </div>
                        </div>
                    )}

                    {status === 'error' && (
                        <div className="mb-4 flex items-center justify-center">
                            <div className="rounded-full bg-red-500 p-2">
                                <svg
                                    className="h-6 w-6 text-white"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                    />
                                </svg>
                            </div>
                        </div>
                    )}

                    <p className="text-sm text-white/80">{message}</p>
                </div>

                {status === 'success' && (
                    <div className="text-xs text-white/60">
                        This tab will close automatically, or you can close it
                        manually.
                    </div>
                )}

                {status === 'error' && (
                    <div className="text-xs text-white/60">
                        Redirecting back to extension...
                    </div>
                )}
            </div>
        </div>
    )
}
